import React, { useEffect, useState } from 'react';
import { FontIcon } from '@fluentui/react';
import SConsciousApiService from '../services/SConsciousApiService';

interface MerchantSuggestion {
    merchant_id: string;
    name: string;
    city?: string;
    street_address?: string;
    postal_code?: string;
    country?: string;
    owning_corporation?: string;
    confidence: string;
    _isNew?: boolean;
}

interface MerchantPickerProps {
    receiptId: string;
    userId: string;
    currentMerchantName: string;
    onSaved: (merchantId: string, merchantName: string) => void;
    onCancel: () => void;
}

const _api = new SConsciousApiService();

const MerchantPicker: React.FC<MerchantPickerProps> = ({ receiptId, userId, currentMerchantName, onSaved, onCancel }) => {
    const [suggestions, setSuggestions] = useState<MerchantSuggestion[]>([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<MerchantSuggestion | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [mode, setMode] = useState<'db' | 'ai'>('db');
    const [aiLoading, setAiLoading] = useState(false);

    // Initial load: DB branch lookup — auto-trigger AI if DB returns nothing
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        _api.getReceiptMerchantBranches(receiptId, userId).then(async result => {
            if (cancelled) return;
            if (result.suggestions.length > 0) {
                setSuggestions(result.suggestions);
                setLoading(false);
                setMode('db');
            } else {
                // No DB branches — fall back to AI analysis
                setLoading(false);
                setAiLoading(true);
                try {
                    const aiResult = await _api.suggestReceiptMerchants(receiptId, userId);
                    if (!cancelled) {
                        setSuggestions(aiResult);
                        setMode('ai');
                    }
                } catch {
                    if (!cancelled) setError('AI analysis failed. Try again.');
                } finally {
                    if (!cancelled) setAiLoading(false);
                }
            }
        });
        return () => { cancelled = true; };
    }, [receiptId, userId]);

    const handleAiRefresh = async () => {
        setAiLoading(true);
        setError(null);
        setSelected(null);
        try {
            const result = await _api.suggestReceiptMerchants(receiptId, userId);
            setSuggestions(result);
            setMode('ai');
        } catch {
            setError('AI analysis failed. Try again.');
        } finally {
            setAiLoading(false);
        }
    };

    const handleSave = async () => {
        if (!selected) return;
        setSaving(true);
        setError(null);
        const newData = selected._isNew ? {
            name: selected.name,
            city: selected.city,
            street_address: selected.street_address,
            postal_code: selected.postal_code,
            country: selected.country,
            owning_corporation: selected.owning_corporation,
        } : undefined;
        const result = await _api.updateReceiptMerchant(receiptId, selected.merchant_id, userId, newData);
        setSaving(false);
        if (result.success) {
            onSaved(result.merchant?.merchant_id || selected.merchant_id, selected.name);
        } else {
            setError('Could not save. Please try again.');
        }
    };

    const confidenceLabel = (c: string) => {
        if (c === 'high') return '●';
        if (c === 'medium') return '◐';
        return '○';
    };

    return (
        <div style={{
            background: 'rgba(0,0,0,0.06)',
            borderRadius: 12,
            padding: '14px 16px',
            marginTop: 4,
        }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10, opacity: 0.6 }}>
                Select merchant for this receipt
            </div>

            {aiLoading ? (
                <div style={{ fontSize: '0.82rem', opacity: 0.55, marginBottom: 10 }}>Analyzing receipt with AI…</div>
            ) : loading ? (
                <div style={{ fontSize: '0.82rem', opacity: 0.55, marginBottom: 10 }}>Looking for branches…</div>
            ) : suggestions.length === 0 ? (
                <div style={{ fontSize: '0.82rem', opacity: 0.55, marginBottom: 10 }}>No matching merchants found.</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                    {suggestions.map(s => (
                        <button
                            key={s.merchant_id}
                            onClick={() => setSelected(s)}
                            style={{
                                fontFamily: 'inherit',
                                background: selected?.merchant_id === s.merchant_id
                                    ? '#000'
                                    : s._isNew ? 'rgba(255,204,0,0.15)' : 'rgba(0,0,0,0.10)',
                                color: selected?.merchant_id === s.merchant_id ? '#ffcc00' : '#000',
                                border: s._isNew && selected?.merchant_id !== s.merchant_id ? '1.5px dashed rgba(0,0,0,0.3)' : 'none',
                                borderRadius: 12,
                                padding: '8px 14px',
                                fontSize: '0.82rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                textAlign: 'left',
                                lineHeight: 1.3,
                            }}
                        >
                            <span style={{ fontSize: '0.7rem', opacity: 0.55, flexShrink: 0 }} title={s._isNew ? 'New merchant' : `Confidence: ${s.confidence}`}>
                                {s._isNew ? '＋' : confidenceLabel(s.confidence)}
                            </span>
                            <span style={{ display: 'flex', flexDirection: 'column' }}>
                                <span>{s.name}{s._isNew && <span style={{ fontSize: '0.7rem', opacity: 0.5, marginLeft: 6 }}>new</span>}</span>
                                {(s.city || s.street_address) && (
                                    <span style={{ fontSize: '0.7rem', opacity: 0.55, fontWeight: 400 }}>
                                        {[s.street_address, s.city, s.postal_code].filter(Boolean).join(', ')}
                                    </span>
                                )}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {error && (
                <div style={{ fontSize: '0.78rem', color: '#e53935', marginBottom: 8 }}>{error}</div>
            )}

            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                    onClick={handleSave}
                    disabled={!selected || saving || aiLoading}
                    style={{
                        fontFamily: 'inherit',
                        flex: 1,
                        background: '#000',
                        color: '#ffcc00',
                        border: 'none',
                        borderRadius: 20,
                        padding: '8px 0',
                        opacity: saving ? 0.35 : 1,
                        cursor: selected && !saving ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <FontIcon iconName="Save" style={{ fontSize: '1rem' }} />
                </button>
                <button
                    onClick={handleAiRefresh}
                    disabled={saving || aiLoading}
                    title={mode === 'ai' ? 'Re-analyze with AI' : 'Analyze receipt with AI'}
                    style={{
                        fontFamily: 'inherit',
                        flex: '0 0 calc(25% - 12px)',
                        background: mode === 'ai' ? 'rgba(0,0,0,0.18)' : 'rgba(0,0,0,0.06)',
                        color: '#000',
                        border: 'none',
                        borderRadius: 20,
                        padding: '8px 0',
                        cursor: saving || aiLoading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: aiLoading ? 0.35 : 1,
                    }}
                >
                    <FontIcon iconName="Robot" style={{ fontSize: '1rem' }} />
                </button>
                <button
                    onClick={onCancel}
                    disabled={saving}
                    style={{
                        fontFamily: 'inherit',
                        flex: '0 0 calc(25% - 12px)',
                        background: 'rgba(0,0,0,0.06)',
                        color: 'rgba(0,0,0)',
                        border: 'none',
                        borderRadius: 20,
                        padding: '8px 0',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <FontIcon iconName="Cancel" style={{ fontSize: '1rem' }} />
                </button>
            </div>
        </div>
    );
};

export default MerchantPicker;
